import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import c from 'classnames';

import { SearchkitWrapper } from '@uncinc/react-searchkit';
import type { SearchkitWrapperProps } from '@uncinc/react-searchkit/dist/components/Wrapper';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import { APP_OPTIONS, SEARCHKIT_HOST_URL } from '../../config/constants';
import GoBackButton from '../GoBackButton';
import {
  facetAttributes,
  highlightAttributes,
  hitsPerPage,
  resultAttributes,
  searchAttributes,
  sorting,
} from './config';

import './style.scss';

// Subcomponents.
import Facets from './Facets';
import HeaderSearchBar from './HeaderSearchBar';
import Overlay, { type SearchForumOverlayRef } from './Overlay';
import Results from './Results';
import SearchBar from './SearchBar';
import SortBy from './SortBy';
import Stats from './Stats';

interface SearchForumSubcomponents {
  Facets: typeof Facets;
  HeaderSearchBar: typeof HeaderSearchBar;
  Overlay: typeof Overlay;
  Results: typeof Results;
  SearchBar: typeof SearchBar;
  SortBy: typeof SortBy;
  Stats: typeof Stats;
}

export type SearchForumWrapperProps = Omit<SearchkitWrapperProps,
'connection' | 'indexName' | 'routing' | 'analytics' | 'onlyFetchWithQuery' | 'hitsPerPage' | 'searchSettings' | 'initialUiState'
>;

// eslint-disable-next-line max-len
const SearchForumWrapper: React.FC<SearchForumWrapperProps> & SearchForumSubcomponents = (props) => {
  const {
    className,
    children,
    ...restProps
  } = props;

  const classNames = c('SearchForum', className);

  return (
    <SearchkitWrapper
      {...restProps}
      className={classNames}
      connection={{ host: SEARCHKIT_HOST_URL }}
      indexName={APP_OPTIONS.parameters.searchkitIndexForumThread}
      routing={false}
      analytics={false}
      onlyFetchWithQuery
      hitsPerPage={hitsPerPage}
      searchSettings={{
        search_attributes: searchAttributes,
        facet_attributes: facetAttributes,
        result_attributes: resultAttributes,
        highlight_attributes: highlightAttributes,
        sorting,
      }}
      initialUiState={{
        [APP_OPTIONS.parameters.searchkitIndexForumThread]: {
          refinementList: {
            entity_type: ['node', 'comment'],
          },
        },
      }}
    >
      {children}
    </SearchkitWrapper>
  );
};

SearchForumWrapper.Facets = Facets;
SearchForumWrapper.HeaderSearchBar = HeaderSearchBar;
SearchForumWrapper.Overlay = Overlay;
SearchForumWrapper.Results = Results;
SearchForumWrapper.SearchBar = SearchBar;
SearchForumWrapper.SortBy = SortBy;
SearchForumWrapper.Stats = Stats;

const SearchForum: React.FC = () => {
  const overlayRef = useRef<SearchForumOverlayRef>(null);
  const location = useLocation();

  const handleBackClick = useCallback(() => {
    if (!overlayRef.current) {
      return;
    }

    overlayRef.current.toggleOverlay();
  }, []);

  useEffect(() => {
    overlayRef.current?.toggleOverlay(true);
  }, [location]);

  return (
    <SearchForumWrapper>
      <SearchForumWrapper.Overlay
        ref={overlayRef}
        Trigger={SearchForumWrapper.HeaderSearchBar}
      >
        <Container>
          <div className="row">
            <div className="col-lg-10 offset-lg-1">
              <SearchForumWrapper.SearchBar />
              <GoBackButton
                className="SearchForum__back"
                onClick={handleBackClick}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 col-lg-5 offset-lg-1">
              <SearchForumWrapper.Facets />
            </div>

            <div className="col-md-6 col-lg-5">
              <div className="SearchForum__stats">
                <SearchForumWrapper.Stats />
                <SearchForumWrapper.SortBy />
              </div>
            </div>
          </div>
        </Container>

        <SearchForumWrapper.Results />
      </SearchForumWrapper.Overlay>
    </SearchForumWrapper>
  );
};

export default SearchForum;
